import { computed, ref } from 'vue';
import { readStorage, readStorageJson, writeStorageJson } from '@/shared/util/browserStorage';

export type ThemeMode = 'light' | 'dark' | 'system';
export type AcademyPalette = 'glintstone' | 'moon' | 'emerald';
export type UiDensity = 'comfortable' | 'compact';
export type MotionPreference = 'full' | 'reduced';
export type FontScale = 'normal' | 'large' | 'extra-large' | 'maximum';
export type ContrastPreference = 'normal' | 'high';

interface PreferencesState {
  mode: ThemeMode;
  palette: AcademyPalette;
  density: UiDensity;
  motion: MotionPreference;
  fontScale: FontScale;
  contrast: ContrastPreference;
}

const STORAGE_KEY = 'ava.admin.preferences.v1';
const LEGACY_PREFERENCES_KEYS = [] as const;
const LEGACY_THEME_KEY = 'ava.theme';

const mode = ref<ThemeMode>('light');
const palette = ref<AcademyPalette>('glintstone');
const density = ref<UiDensity>('comfortable');
const motion = ref<MotionPreference>('full');
const fontScale = ref<FontScale>('normal');
const contrast = ref<ContrastPreference>('normal');
const systemPrefersDark = ref(false);
let restored = false;
let media: MediaQueryList | null = null;
let mediaHandler: ((event: MediaQueryListEvent) => void) | null = null;

function isMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}
function isDensity(value: unknown): value is UiDensity {
  return value === 'comfortable' || value === 'compact';
}
function isMotion(value: unknown): value is MotionPreference {
  return value === 'full' || value === 'reduced';
}
function isFontScale(value: unknown): value is FontScale {
  return value === 'normal' || value === 'large' || value === 'extra-large' || value === 'maximum';
}

export function useUiPreferences() {
  const isDark = computed(() => mode.value === 'dark' || (mode.value === 'system' && systemPrefersDark.value));
  function persist(): void {
    writeStorageJson(STORAGE_KEY, {
      mode: mode.value,
      palette: palette.value,
      density: density.value,
      motion: motion.value,
      fontScale: fontScale.value,
      contrast: contrast.value,
    } satisfies PreferencesState);
  }

  function applyDocumentPreferences(): void {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.rayaDensity = density.value;
    document.documentElement.dataset.rayaMotion = motion.value;
    document.documentElement.dataset.rayaFont = fontScale.value;
    document.documentElement.dataset.rayaContrast = contrast.value;
    document.documentElement.dataset.rayaPalette = palette.value;
    document.documentElement.dataset.rayaScheme = isDark.value ? 'dark' : 'light';
    document.documentElement.dataset.theme = isDark.value ? 'dark' : 'light';
    document.documentElement.style.colorScheme = isDark.value ? 'dark' : 'light';
  }

  function applyTheme(): void {
    applyDocumentPreferences();
  }

  function detachSystemListener(): void {
    if (media && mediaHandler) media.removeEventListener('change', mediaHandler);
    media = null;
    mediaHandler = null;
  }

  function attachSystemListener(): void {
    detachSystemListener();
    if (typeof window === 'undefined' || mode.value !== 'system') return;
    media = window.matchMedia('(prefers-color-scheme: dark)');
    systemPrefersDark.value = media.matches;
    mediaHandler = (event) => {
      systemPrefersDark.value = event.matches;
      applyTheme();
    };
    media.addEventListener('change', mediaHandler);
  }

  function restore(): void {
    if (typeof window !== 'undefined') systemPrefersDark.value = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    if (!restored) {
      restored = true;
      const stored = readStorageJson(STORAGE_KEY);
      let saved: Partial<PreferencesState> | null = stored && typeof stored === 'object'
        ? stored as Partial<PreferencesState>
        : null;
      if (!saved) {
        for (const legacyKey of LEGACY_PREFERENCES_KEYS) {
          const legacyPreferences = readStorageJson(legacyKey);
          if (!legacyPreferences || typeof legacyPreferences !== 'object') continue;
          const legacySaved = legacyPreferences as Partial<PreferencesState>;
          saved = {
            ...legacySaved,
            // Preferências de versões anteriores permanecem independentes do portal.
            // Uma escolha do usuário é persistida no namespace administrativo.
            mode: 'light',
          };
          break;
        }
      }
      if (!saved) {
        const legacy = readStorage(LEGACY_THEME_KEY);
        if (legacy === 'light' || legacy === 'dark') saved = { mode: legacy }; // Preserva a escolha anterior deste administrativo.
      }
      if (isMode(saved?.mode)) mode.value = saved.mode;
      // A identidade verde é única; preferências antigas não reativam a paleta roxa.
      palette.value = 'glintstone';
      if (isDensity(saved?.density)) density.value = saved.density;
      if (isMotion(saved?.motion)) motion.value = saved.motion;
      if (isFontScale(saved?.fontScale)) fontScale.value = saved.fontScale;
      if (saved?.contrast === 'normal' || saved?.contrast === 'high') contrast.value = saved.contrast;
    }
    applyTheme();
    attachSystemListener();
  }

  function setMode(value: ThemeMode): void {
    mode.value = value;
    attachSystemListener();
    applyTheme();
    persist();
  }
  function setPalette(value: AcademyPalette): void {
    palette.value = value;
    applyTheme();
    persist();
  }
  function setDensity(value: UiDensity): void {
    density.value = value;
    applyDocumentPreferences();
    persist();
  }
  function setMotion(value: MotionPreference): void {
    motion.value = value;
    applyDocumentPreferences();
    persist();
  }
  function setFontScale(value: FontScale): void {
    fontScale.value = value;
    applyDocumentPreferences();
    persist();
  }

  function setContrast(value: ContrastPreference): void {
    contrast.value = value;
    applyDocumentPreferences();
    persist();
  }

  return {
    mode,
    palette,
    density,
    motion,
    fontScale,
    contrast,
    isDark,
    restore,
    setMode,
    setPalette,
    setDensity,
    setMotion,
    setFontScale,
    setContrast,
  };
}
